import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from "../../configs/axiosInstance";

export const initialState = {
	loading: false,
	detailLoading: false,
	error: '',
	success: false,
	message: '',
	messageType: '',
	domainList: [],
	domainDetails: null,
	lastScanId: null,
	domainDetailsVulnerabilities: null,
	domainDetailsVulnerabilityDetails: {},
	domainHistoryList: [],
	domainHistoryNameList: [],
	vulnerabilityRemediationLoading: false,
	vulnerabilityRemediationDetails: null,
	vulnerabilityRemediationError: false,
};

export const networkQuery = createAsyncThunk(
	'network/networkQuery',
	async (data, { rejectWithValue, dispatch }) => {
		const name = data.name;
		const taskScheduler = {
			frequency: data.taskScheduler.frequency,
			period: data.taskScheduler.period.toUpperCase(),
		};

		try {
			const response = await API.post('/account-service/v1/domains', {
				name,
				taskScheduler,
			});

			dispatch(domainQuerySuccess(response.data));

			return response.data;
		} catch (error) {
			if (error.response.data.status == 50) {
				return rejectWithValue(
					dispatch(
						domainQueryError(
							'Domain daha önce eklenmiş. Lütfen farklı bir domain deneyiniz.'
						)
					)
				);
			}
			return rejectWithValue(
				dispatch(
					domainQueryError(error.response?.data?.error || 'Bir Hata Oluştu')
				)
			);
		}
	}
);

export const getDomainList = createAsyncThunk(
	'network/getDomainList',
	async (data, { rejectWithValue, dispatch }) => {
		const name = data.name || '';
		const ip = data.ip || '';
		const createdDate = data.createdDate || '';

		try {
			const response = await API.post('/account-service/v1/domains/pageable', {
				name,
				ip,
				createdDate,
			});

			dispatch(domainListSuccess(response.data));

			return response.data;
		} catch (error) {
			return rejectWithValue(
				dispatch(
					domainListError(error.response?.data?.error || 'Bir Hata Oluştu')
				)
			);
		}
	}
);

export const deleteDomain = createAsyncThunk(
	'network/deleteDomain',
	async (data, { rejectWithValue, dispatch }) => {
		try {
			const response = await API.delete(`/account-service/v1/domains/${data}`);

			dispatch(domainDeleteSuccess({ id: data }));

			return response.data;
		} catch (error) {
			return rejectWithValue(
				dispatch(
					domainDeleteError(error.response?.data?.error || 'Bir Hata Oluştu')
				)
			);
		}
	}
);

export const domainDetail = createAsyncThunk(
	'network/domainDetail',
	async (data, { rejectWithValue, dispatch }) => {
		try {
			const response = await API.get(
				`/account-service/v1/domains/${data}/details`
			);

			dispatch(domainDetailsSuccess(response.data));

			return response.data;
		} catch (error) {
			return rejectWithValue(
				dispatch(
					domainDetailsError(error.response?.data?.error || 'Bir Hata Oluştu')
				)
			);
		}
	}
);

export const getDomainHistoryList = createAsyncThunk(
	'network/getDomainHistoryList',
	async (data, { rejectWithValue, dispatch }) => {
		try {
			const response = await API.get(
				`/account-service/v1/domains/history/${data.id}`,
				{ params: data.params }
			);

			dispatch(domainHistoryListSuccess(response.data));

			return response.data;
		} catch (error) {
			return rejectWithValue(
				dispatch(
					domainHistoryListError(
						error.response?.data?.error || 'Bir Hata Oluştu'
					)
				)
			);
		}
	}
);

export const getDomainHistoryNameList = createAsyncThunk(
	"network/getDomainHistoryNameList",
	async (data, { rejectWithValue, dispatch }) => {
		try {
			const response = await API.get(
				`/account-service/v1/domains/${data}/history`,
			);

			dispatch(setLastHistoryNamesListParentId(data));
			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const deleteDomainHistory = createAsyncThunk(
	"network/deleteDomainHistory",
	async (id, { rejectWithValue }) => {
		try {
			await API.delete(`/domain-service/v1/domain/history/${id}`);
			return id;
		} catch (e) {
			return rejectWithValue(e);
		}
	}
);

export const getDetailVulnerabilities = createAsyncThunk(
	"network/getDetailVulnerabilities",
	async (data, { rejectWithValue }) => {
		try {
			const isThirdParty = localStorage.getItem("userType") === "THIRD_PARTY";
			const response = isThirdParty
				? await API.get(`/account-service/v1/third-party-portal/vulnerabilities`)
				: await API.get(`/account-service/v1/domains/${data}/vulnerabilities`);
			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const getDetailVulnerabilityDetails = createAsyncThunk(
	"network/getDetailVulnerabilityDetails",
	async (data, { rejectWithValue }) => {
		try {
			const pluginId = typeof data === 'object' ? data.pluginId : data;
			const scanId = typeof data === 'object' ? data.scanId : undefined;
			const hostId = typeof data === 'object' ? data.hostId : undefined;

			const params = {};
			if (scanId) params.scanId = scanId;
			if (hostId) params.hostId = hostId;

			const response = await API.get(
				`/account-service/v1/plugins/${pluginId}`,
				{ params }
			);
			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const getDetailedRemediationSteps = createAsyncThunk(
	"network/getDetailedRemediationSteps",
	async (data, { rejectWithValue }) => {
		try {
			const response = await API.post(
				`/issue-service/v1/open-ai/plugin-remedy-vulnerability`,
				data?.data,
				{ params: data?.params }
			);
			return response.data;
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const approveRemediationStep = createAsyncThunk(
	'network/approveRemediationStep',
	async ({ stepId, approved }, { rejectWithValue }) => {
		try {
			await API.post('/issue-service/v1/open-ai/remedy/step/approve', {
				stepId,
				approved,
			});
			return { stepId, approved };
		} catch (error) {
			return rejectWithValue(error.response?.data || error.message);
		}
	}
);

export const networkSlice = createSlice({
	name: 'network',
	initialState,
	reducers: {
		domainQuerySuccess: (state, action) => {
			state.loading = false;
			state.success = true;
			state.message = 'Domain sorgulama başarılı';
			state.messageType = 'success';
			state.error = '';
			state.data = action.payload;
		},
		domainQueryError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		domainListSuccess: (state, action) => {
			state.domainList = action.payload;
			state.loading = false;
			state.success = true;
			state.message = 'Domain listesi başarılı';
			state.messageType = 'success';
			state.error = '';
		},
		domainListError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		domainDetailsClear: (state) => {
			state.domainDetails = null;
			state.domainDetailsVulnerabilities = null;
			state.domainDetailsVulnerabilityDetails = [];
		},
		domainDeleteSuccess: (state, action) => {
			state.domainList.content = state.domainList.content.filter(
				(item) => item.id !== action.payload.id
			);
			state.loading = false;
			state.success = true;
			state.message = 'Domain silme başarılı';
			state.messageType = 'success';
			state.error = '';
		},
		domainDeleteError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		domainDetailsSuccess: (state, action) => {
			state.domainDetails = action.payload;
			state.loading = false;
			state.detailLoading = false;
			state.success = true;
			state.message = 'Domain detayları başarılı';
			state.messageType = 'success';
			state.error = '';
		},
		domainDetailsError: (state, action) => {
			state.loading = false;
			state.detailLoading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		domainHistoryListSuccess: (state, action) => {
			state.domainHistoryList = action.payload;
			state.loading = false;
			state.success = true;
			state.message = 'Domain geçmiş listesi başarılı';
			state.messageType = 'success';
			state.error = '';
		},
		domainHistoryListError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		domainHistoryDeleteSuccess: (state, action) => {
			state.domainHistoryList.content = state.domainHistoryList.content.filter(
				(item) => item.id !== action.payload.id
			);
			state.loading = false;
			state.success = true;
			state.message = 'Domain geçmiş silme başarılı';
			state.messageType = 'success';
			state.error = '';
		},
		domainHistoryDeleteError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.success = false;
			state.message = action.payload || 'Bir Hata Oluştu';
			state.messageType = 'error';
		},
		resetVulnerabilityDetails: (state) => {
			state.domainDetailsVulnerabilityDetails = {};
			state.detailLoading = false;
		},
		setLastHistoryNamesListParentId: (state, action) => {
			state.lastScanId = action.payload;
		},
		resetDetailedRemediationSteps: (state) => {
			state.vulnerabilityRemediationDetails = null;
			state.vulnerabilityRemediationLoading = false;
			state.vulnerabilityRemediationError = false;
		},
		resetVulnerabilityRemediationError: (state) => {
			state.vulnerabilityRemediationError = false;
		}
	},

	extraReducers: (builder) => {
		builder
			.addCase(networkQuery.pending, (state) => {
				state.loading = true;
			})

			.addCase(getDomainList.pending, (state) => {
				state.loading = true;
			})

			.addCase(deleteDomain.pending, (state) => {
				state.loading = true;
			})

			.addCase(domainDetail.pending, (state) => {
				state.detailLoading = true;
				state.loading = true;
				state.domainDetails = null;
			})
			.addCase(domainDetail.fulfilled, (state, action) => {
				state.domainDetails = action.payload;
				state.loading = false;
				state.detailLoading = false;
			})

			.addCase(getDomainHistoryList.pending, (state) => {
				state.loading = true;
				state.domainHistoryList = [];
			})
			.addCase(getDomainHistoryList.fulfilled, (state, action) => {
				state.domainHistoryList = action.payload;
				state.loading = false;
			})

			.addCase(getDomainHistoryNameList.pending, (state) => {
				state.domainHistoryNameList = [];
			})
			.addCase(getDomainHistoryNameList.fulfilled, (state, action) => {
				state.domainHistoryNameList = action.payload;
				state.loading = false;
				state.success = true;
				state.message = 'Domain geçmiş listesi başarılı';
				state.messageType = 'success';
				state.error = '';
			})
			.addCase(getDomainHistoryNameList.rejected, (state, action) => {
				state.domainHistoryNameList = [];
				state.loading = false;
				state.error = action.payload;
				state.success = false;
				state.message = action.payload || 'Bir Hata Oluştu';
				state.messageType = 'error';
			})

			.addCase(deleteDomainHistory.pending, (state) => {
				state.loading = true;
			})
			.addCase(deleteDomainHistory.fulfilled, (state, action) => {
				state.domainHistoryList = state.domainHistoryList.filter(x => x.id !== action.payload);
				state.loading = false;
			})

			.addCase(getDetailVulnerabilities.pending, (state) => {
				state.loading = true;
				state.domainDetailsVulnerabilities = null;
			})
			.addCase(getDetailVulnerabilities.fulfilled, (state, action) => {
				state.domainDetailsVulnerabilities = action.payload;
				state.loading = false;
			})
			.addCase(getDetailVulnerabilities.rejected, (state) => {
				state.loading = false;
			})

			.addCase(getDetailVulnerabilityDetails.pending, (state) => {
				state.detailLoading = true;
			})
			.addCase(getDetailVulnerabilityDetails.fulfilled, (state, action) => {
				const pluginId = action.meta.arg?.pluginId ?? action.meta.arg;
				state.domainDetailsVulnerabilityDetails[pluginId] = action.payload;
				state.detailLoading = false;
			})
			.addCase(getDetailVulnerabilityDetails.rejected, (state) => {
				state.domainDetailsVulnerabilityDetails = [];
				state.detailLoading = false;
			})
			.addCase(getDetailedRemediationSteps.pending, (state) => {
				state.vulnerabilityRemediationLoading = true;
				state.vulnerabilityRemediationError = false;
			})
			.addCase(getDetailedRemediationSteps.fulfilled, (state, action) => {
				if (action?.payload == undefined) {
					state.vulnerabilityRemediationError = true;
				}
				state.vulnerabilityRemediationDetails = action?.payload;
				state.vulnerabilityRemediationLoading = false;
			})
			.addCase(getDetailedRemediationSteps.rejected, (state) => {
				state.vulnerabilityRemediationLoading = false;
				state.vulnerabilityRemediationError = true;
			})

			.addCase(approveRemediationStep.fulfilled, (state, action) => {
				if (state.vulnerabilityRemediationDetails?.steps) {
					const { stepId, approved } = action.payload;
					const stepIndex = state.vulnerabilityRemediationDetails.steps.findIndex(
						s => s.stepId === stepId
					);
					if (stepIndex !== -1) {
						state.vulnerabilityRemediationDetails.steps[stepIndex].userApproval = approved;
					}
				}
			});
	},
});

export const {
	domainQuerySuccess,
	domainQueryError,
	domainListError,
	domainListSuccess,
	domainDetailsClear,
	domainDeleteSuccess,
	domainDeleteError,
	domainDetailsSuccess,
	domainDetailsError,
	domainHistoryListSuccess,
	domainHistoryListError,
	domainHistoryDeleteError,
	domainHistoryDeleteSuccess,
	resetVulnerabilityDetails,
	setLastHistoryNamesListParentId,
	resetDetailedRemediationSteps,
	resetVulnerabilityRemediationError
} = networkSlice.actions;

export default networkSlice.reducer;